import type { ApiError } from '@/api/http';
import { type ApiProblemType, type SubmissionProblem } from '@/api/interviews';

import type { FillingInForm } from './fillingInFields';
import { commentIsRequired, NOTHING_CHOSEN } from './scoreOptions';

/**
 * §6.6's **completeness gate**, on the client side — the three rules the server enforces on submission,
 * the mapping from a refusal entry to the field it is about, and nothing else.
 *
 * ⚠️ **The server's answer is authoritative and this module is a courtesy.** `POST
 * /api/v1/interviews/{id}/submit/` answers `422 SubmissionRefused` with **every** problem at once
 * (`backend/src/apps/interviews/services.py` → `submission_problems`), and that path is handled and
 * tested on its own: a client that trusted its own copy of the rules would refuse a submission the
 * server would have accepted, or — worse — send one it will refuse and have no words for the answer.
 * What the local copy buys is the round trip: pressing **Submit** on an interview that plainly is not
 * finished marks the fields immediately rather than opening a confirmation for an act that is about to
 * be refused. `submit.test.tsx` asserts the two agree, rule for rule.
 *
 * ⚠️ **The server's `msg` is never rendered** (§8, §11 — no `detail`, no code, no server sentence). Every
 * entry is reduced here to *which field* and *which rule*, and the wording is the screen's own
 * (`interviews.json` → `filling.submit.problem.*`). That is also what keeps a candidate's data off the
 * screen from the one body most likely to carry it: `SubmissionProblem`'s own docstring records that a
 * 422 is *"the easiest place in the product for a candidate's personal data to escape"*, which is why the
 * server sends no comment text and no rating — and the client reading `type`/`loc`/`question_id` instead
 * of the prose is the same rule kept on this side.
 */

/**
 * Which rule was broken, as far as the screen needs to know.
 *
 * ⚠️ **The server's kinds come from the document since backend TASK-106** — `SubmissionProblemType`,
 * which the API now declares — so this type no longer re-spells them. It used to:
 * `'comment_required' | 'missing' | 'unknown'`, narrowed by string comparison, which meant a **third**
 * kind added on the server arrived here as `unknown` and rendered a generic sentence, silently, with
 * every test on both sides green. (There *was* a third: the autosave's `value_error`, which the submit
 * route never raises and which declaring the field on the server surfaced immediately.)
 *
 * `unknown` stays, and is now the only locally invented member: it is what a **server ahead of this
 * client** reads as, and it is not defensive padding — it is the difference between *"something on this
 * interview is not finished"*, which is true and useful, and a screen that silently drops a refusal it
 * could not name while somebody presses a button that keeps failing.
 */
export type ProblemReason = ApiProblemType | 'unknown';

/** A field of §6.6's form, as a refusal names it. A score is named by its **question id**, never by index. */
export type ProblemField =
  | { kind: 'score'; questionId: number }
  | { kind: 'overall_recommendation' }
  | { kind: 'overall_comment' };

export interface FieldProblem {
  field: ProblemField;
  reason: ProblemReason;
}

/**
 * A refusal the client could not place on a field — a `loc` it does not know and no `question_id`.
 *
 * It is kept and said out loud rather than discarded, because the alternative is a submission that fails
 * with nothing on screen to act on. It carries no text from the server.
 */
export interface UnplacedProblem {
  reason: ProblemReason;
}

export interface Refusal {
  fields: FieldProblem[];
  unplaced: UnplacedProblem[];
}

export const NOTHING_REFUSED: Refusal = { fields: [], unplaced: [] };

/**
 * ⚠️ **The DOM id of the field a problem is about, and the reason it is derived rather than generated.**
 *
 * Every other field on this screen takes its id from `useId`, which is right where the id is only a
 * `label`'s `htmlFor`. These three are also **scroll and focus targets named by a server response**, and
 * a refusal arrives as `question_id: 31` — so the id has to be computable from the refusal alone, which
 * a generated one is not. The screen renders one interview and a question appears once in it, so the id
 * is unique by construction.
 */
export const problemFieldId = (field: ProblemField): string =>
  field.kind === 'score'
    ? `interview-score-comment-${field.questionId}`
    : `interview-${field.kind}`;

/** Whether these two name the same field — the lookup every block does for its own. */
export const sameField = (a: ProblemField, b: ProblemField): boolean =>
  a.kind === 'score' && b.kind === 'score' ? a.questionId === b.questionId : a.kind === b.kind;

/** This field's problem, if the last refusal named it. */
export const problemFor = (refusal: Refusal, field: ProblemField): FieldProblem | undefined =>
  refusal.fields.find((problem) => sameField(problem.field, field));

export const isRefused = (refusal: Refusal): boolean =>
  refusal.fields.length > 0 || refusal.unplaced.length > 0;

/**
 * Every kind the API declares, mapped to a rule **this screen has a sentence for**.
 *
 * ⚠️ **A `Record` over the generated union, not a membership test**, and that is the whole gain from
 * backend TASK-106: the mapping is now **exhaustive by construction**, so a kind added on the server is
 * a `tsc` error *here*, at the table that owns the sentences, instead of a silent `unknown` in a
 * browser. The screen re-spelled these strings before that, and there was already one it did not know
 * about.
 *
 * `value_error` maps to `unknown` **deliberately, not for want of a sentence**: it is the *autosave's*
 * refusal — a question id that is not this interview's — which the submit route never raises and which
 * names no field a reader could fix.
 */
const REASON_OF: Record<ApiProblemType, ProblemReason> = {
  comment_required: 'comment_required',
  missing: 'missing',
  value_error: 'unknown',
};

const reasonOf = (type: string): ProblemReason => REASON_OF[type as ApiProblemType] ?? 'unknown';

/**
 * Where a refusal entry belongs on the form — `question_id` first, then `loc`.
 *
 * `question_id` is the field the backend added for exactly this (its docstring: *"how the screen scrolls
 * the offending question into view"*), and it is checked first because a score's `loc` is
 * `["scores", "comment"]` — the same two segments for every question, so `loc` alone cannot say **which**
 * one. An entry with neither is unplaceable and stays that way; guessing a field would put a message
 * under something that is not the problem.
 */
const fieldOf = (problem: SubmissionProblem): ProblemField | null => {
  if (typeof problem.question_id === 'number') {
    return { kind: 'score', questionId: problem.question_id };
  }
  if (problem.loc.includes('overall_recommendation')) return { kind: 'overall_recommendation' };
  if (problem.loc.includes('overall_comment')) return { kind: 'overall_comment' };
  return null;
};

/**
 * The 422 of the submission, as fields and rules — or `null` when this failure is not that.
 *
 * ⚠️ **`bodyWith('detail')` and never a hand-written parse of the response.** It is `http.ts`'s one
 * mechanism for a refusal that is *part of the interaction* rather than a failure, and it checks the
 * field is there before the body is believed — which matters here because `detail` is a **list** on this
 * route and a *string* on every `ErrorResponse` in the API (`http.ts`'s `detailOf` records the same
 * asymmetry from the other side). A 422 whose body is neither is a refusal with no problems in it, and
 * that reads as *unplaced* rather than as *nothing was wrong*.
 */
export function refusalOf(error: ApiError): Refusal | null {
  if (error.status !== 422) return null;

  const detail = error.bodyWith('detail')?.detail;
  if (!Array.isArray(detail)) return { fields: [], unplaced: [{ reason: 'unknown' }] };

  const refusal: Refusal = { fields: [], unplaced: [] };
  for (const problem of detail) {
    const reason = reasonOf(problem.type);
    const field = fieldOf(problem);
    if (field === null) refusal.unplaced.push({ reason });
    else refusal.fields.push({ field, reason });
  }

  return isRefused(refusal) ? refusal : { fields: [], unplaced: [{ reason: 'unknown' }] };
}

/**
 * ⚠️ **The client's copy of the server's three rules, in the server's own order.**
 *
 * `submission_problems` in `backend/src/apps/interviews/services.py`:
 *
 *  1. **a comment is required at 1, 2 and 5** — `MANDATORY_COMMENT_RATINGS`, and *"a 5 with no
 *     explanation is as useless six months later as a 1 with none"*. Whitespace is not a comment
 *     (`score.comment.strip()`), so `.trim()` here;
 *  2. **the overall recommendation is required** — a draft may sit without one (ADR 0007), a submitted
 *     interview may not;
 *  3. **the overall comment is required**, and whitespace is not one either.
 *
 * ⚠️ **An unanswered question is *not* a problem, on either side.** §6.6: *"a skipped question is
 * information"* — somebody who ran out of time, or judged a question irrelevant, has said something by
 * leaving it blank, and a form that refused to submit over it would be answered with a fabricated 3,
 * which is worse than a gap because it is indistinguishable from an opinion. So there is no rule here
 * about a missing rating, and no control on this screen requires one.
 *
 * The order is the order the screen renders — scores in frozen bank order (ADR 0004), then the two
 * overall fields — so *"the first problem"* is the topmost one on screen, which is where the focus goes.
 */
export function refusalInForm(values: FillingInForm): Refusal {
  const fields: FieldProblem[] = [];

  for (const score of values.scores) {
    if (commentIsRequired(score.rating) && score.comment.trim() === '') {
      fields.push({
        field: { kind: 'score', questionId: score.question_id },
        reason: 'comment_required',
      });
    }
  }

  if (values.overall_recommendation === NOTHING_CHOSEN) {
    fields.push({ field: { kind: 'overall_recommendation' }, reason: 'missing' });
  }

  if (values.overall_comment.trim() === '') {
    fields.push({ field: { kind: 'overall_comment' }, reason: 'missing' });
  }

  return fields.length === 0 ? NOTHING_REFUSED : { fields, unplaced: [] };
}

/**
 * **The sentence a problem renders as — a key of `interviews.json`, never the server's `msg`.**
 *
 * The mapping lives beside the one that finds the field, because they are two halves of one decision: the
 * refusal names a field and a rule, and this screen owns what that reads as. A rule the client cannot
 * name still gets a sentence rather than silence.
 */
export const problemMessageKey = ({ field, reason }: FieldProblem): string => {
  if (reason === 'unknown') return 'filling.submit.problem.unknown';
  if (field.kind === 'score') return 'filling.submit.problem.comment';
  if (field.kind === 'overall_recommendation') return 'filling.submit.problem.recommendation';
  return 'filling.submit.problem.overallComment';
};

/**
 * §6.6: *"Missing required comments block submission, with the fields scrolled into view."*
 *
 * ⚠️ **The focus target is the control and not the box around it.** A score's comment and the overall
 * comment are `textarea`s and focus themselves; the overall recommendation is a Radix `RadioGroup`, whose
 * root is a `div` with a roving tabindex on its **items** — focusing the root moves nothing, so the first
 * radio is focused instead. One helper does both, because a caller that had to know which is which would
 * be the mapping in a second place.
 *
 * `scrollIntoView` is called optionally because jsdom does not implement it (`submit.test.tsx` installs a
 * spy and asserts *which* element was asked to scroll — the assertion the criterion needs, and one a
 * `try`/`catch` would have swallowed).
 */
export function revealProblem(field: ProblemField): void {
  const box = document.getElementById(problemFieldId(field));
  if (box === null) return;

  box.scrollIntoView?.({ block: 'center' });

  const focusable = box.matches('textarea, input, select, button')
    ? box
    : box.querySelector<HTMLElement>('button, textarea, input, select');
  focusable?.focus();
}
